import {
  CalendarOutlined,
  HistoryOutlined,
  LoadingOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Drawer, Empty, message, Select, Timeline } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { useSAUsers } from '../../custom-hooks/sa-users';
import { queryClient } from '../../QueryClient';
import { fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';
import { RFITicket } from './types';

const getAllHistory = async (
  ticketId: number,
  filterColumns = '',
  page = 1,
  prev = []
) => {
  try {
    const resp = await fetchAPI('user/activity', {
      payload: {
        table_name: 'rfi_ticketing',
        row_id: ticketId,
        per_page: 30,
        value_type: filterColumns || undefined,
      },
    });

    const all = [...prev, ...resp.items];
    if (resp.total > 30 * page) {
      return getAllHistory(ticketId, filterColumns, page + 1, all);
    }

    return all;
  } catch (error) {
    message.error('Failed get ticket history');
    return prev;
  }
};

const SubmittedTicketHistory = (props: { record: RFITicket }) => {
  const [visible, setVisible] = useState(false);
  // const [filterColumns, setFilterColumns] = useState([]);
  const [filterColumns, setFilterColumns] = useState('');
  const ticketHistory = useQuery<{
    list: Array<{
      updated_at: string;
      created_at: string;
      id: number;
      user_id: number;
      table_name: string;
      value_type: string;
      row_id: number;
      old_value: string;
      new_value: string;
      user: {
        id: number;
        username: string;
        phone_number: string;
        full_name: string;
        role: string;
        team: string;
      };
    }>;
    loading: boolean;
  }>(
    ['rfi-ticket-history', visible, props.record?.id, filterColumns],
    async () => {
      if (!visible || !props.record?.id) {
        return { list: [], loading: true };
      }

      const resp = await getAllHistory(props.record.id, filterColumns);
      return {
        list: resp,
        loading: false,
      };
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        list: [],
        loading: true,
      },
    }
  );

  const headerTitle = {
    rfi_type: 'Type of Issue',
    marketplace_name: 'Marketplace Name',
    sme_marketplace_id: 'Marketplace',
    revised_dateline: 'Revised Dateline',
    resolution_date: 'Resolution Date',
    action_by_id: 'Assigned Staff',
    status: 'Status',
    entity_id: 'Buyer Entity',
    comments: 'Note',
  };

  return (
    <>
      <Drawer
        visible={visible}
        onClose={() => setVisible(false)}
        closable={false}
        bodyStyle={{ width: 500 }}
        title={
          <span style={{ fontSize: 12 }}>
            {props.record.rfi_number} History
            <UndoOutlined
              style={{ marginLeft: 10 }}
              onClick={() =>
                queryClient.invalidateQueries(['rfi-ticket-history'])
              }
            />
          </span>
        }
      >
        <Select
          style={{ width: 200, margin: '0 0 15px' }}
          placeholder="Select Column"
          // mode="multiple"
          options={Object.keys(headerTitle).map(headerKey => ({
            value: headerKey,
            label: headerTitle[headerKey],
          }))}
          value={filterColumns || undefined}
          onChange={values => setFilterColumns(values)}
          allowClear
        />
        {ticketHistory.isLoading || ticketHistory.data.loading ? (
          <div style={{ textAlign: 'center', padding: 10 }}>
            <LoadingOutlined />
          </div>
        ) : !ticketHistory.data.list.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No activity yet"
          />
        ) : (
          <>
            <Timeline>
              {ticketHistory.data.list.map(item => {
                return (
                  <Timeline.Item
                    dot={<CalendarOutlined style={{ fontSize: 12 }} />}
                    style={{ paddingBottom: 5 }}
                  >
                    <b style={{ fontWeight: 600, fontSize: 12 }}>
                      {headerTitle[item.value_type] || item.value_type}
                    </b>
                    <p style={{ fontSize: 12, margin: 0 }}>
                      From:{' '}
                      {
                        <HistoryValue
                          historyValueType={item.value_type}
                          value={item.old_value}
                        />
                      }
                    </p>
                    <p style={{ fontSize: 12, margin: 0 }}>
                      To:{' '}
                      {
                        <HistoryValue
                          historyValueType={item.value_type}
                          value={item.new_value}
                        />
                      }
                    </p>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 10,
                        opacity: '0.7',
                      }}
                    >
                      {formatUTCTimeToLocal(
                        item?.created_at,
                        'DD MMM YYYY HH:mm'
                      )}
                      <div style={{ fontSize: '11px' }}>
                        <UserOutlined /> By {item?.user?.full_name}
                      </div>
                    </span>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </>
        )}
      </Drawer>
      <HistoryOutlined
        onClick={() => {
          setVisible(true);
          setFilterColumns('');
        }}
        style={{ cursor: 'pointer' }}
      />
    </>
  );
};

const HistoryValue = (props: { historyValueType: string; value: string }) => {
  const user = useSelector((state: AppState) => state.user);
  const saUsers = useSAUsers(user);

  const valueType = {
    rfi_type: 'string',
    marketplace_name: 'string',
    sme_marketplace_id: 'rfi_marketplace',
    revised_dateline: 'date',
    resolution_date: 'date',
    action_by_id: 'user',
    status: 'string',
    entity_id: 'entity',
    comments: 'string',
  }[props.historyValueType];

  if (!props.value) {
    return <span>None</span>;
  }

  if (valueType === 'date') {
    return <span>{formatUTCTimeToLocal(props.value, 'DD MMM YYYY')}</span>;
  }

  if (valueType === 'user') {
    const userObj = saUsers.data.list.find(
      user => user.id === Number(props.value)
    );
    return <span>{userObj?.full_name || props.value}</span>;
  }

  if (valueType === 'entity') {
    return <EntityValue id={Number(props.value)} />;
  }

  return <span>{props.value}</span>;
};

const EntityValue = (props: { id: number }) => {
  const entity = useQuery(
    ['entity-detail', props.id],
    async () => {
      try {
        const resp = await fetchAPI(`entity/${props.id}/lite`);

        return resp;
      } catch (error) {
        message.error(`Failed get buyer entity detail (${props.id})`);
        return null;
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: null,
    }
  );

  return <span>{entity.data?.name || 'Not Found'}</span>;
};

export default SubmittedTicketHistory;
